import '@/styles/globals.css'

import type { FC, ReactNode } from 'react'
import type { AppProps } from 'next/app'

import { useEffect } from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import localFont from 'next/font/local'
import { DM_Sans, Gochi_Hand } from 'next/font/google'

import { PageTransition } from '@/components/generals/page-transition'

const sans = DM_Sans({
    subsets: ['latin'],
    weight: ['400', '500', '700']
})

const script = Gochi_Hand({
    subsets: ['latin'],
    weight: '400'
})

const display = localFont({
    src: [
        {
            path: '../../public/fonts/LibreCaslonCondensed-400.woff2',
            weight: '400',
            style: 'normal'
        },
        {
            path: '../../public/fonts/LibreCaslonCondensed-400-italic.woff2',
            weight: '400',
            style: 'italic'
        },
        {
            path: '../../public/fonts/LibreCaslonCondensed-500.woff2',
            weight: '500',
            style: 'normal'
        },
        {
            path: '../../public/fonts/LibreCaslonCondensed-600.woff2',
            weight: '600',
            style: 'normal'
        }
    ],
    display: 'swap',
    fallback: ['Georgia', 'Times New Roman', 'serif']
})

const fontVariables = `:root{--font-app-sans:${sans.style.fontFamily};--font-app-mono:${sans.style.fontFamily};--font-app-heading:${display.style.fontFamily};--font-app-script:${script.style.fontFamily}}`

const App: FC<AppProps> = ({ Component, pageProps }): ReactNode => {
    const router = useRouter()

    useEffect(() => {
        if (typeof window === 'undefined' || !('scrollRestoration' in window.history)) return

        // The browser's own scroll restoration fights the SPA navigation and
        // keeps the previous page's scroll position instead of starting the
        // new page at the top, so we take it over manually.
        const previous = window.history.scrollRestoration
        window.history.scrollRestoration = 'manual'

        return () => {
            window.history.scrollRestoration = previous
        }
    }, [])

    useEffect(() => {
        const scrollToTop = (url: string): void => {
            // A link to an in-page anchor (e.g. `/#book`) should still jump
            // to that anchor rather than being forced to the top.
            if (url.includes('#')) return

            // `globals.css` sets `html { scroll-behavior: smooth }` site-wide,
            // which turns this into an animated scroll that can get cut off
            // by the incoming page's layout shifting under it (images,
            // reveals, different page height). Forcing 'instant' bypasses
            // that CSS setting so the jump always lands exactly at the top,
            // every time, before the new page finishes painting.
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        }

        // Fire on routeChangeStart, not routeChangeComplete: this way the
        // viewport is already at the top before the new page's content
        // mounts, instead of jumping after the reader has seen a frame of
        // the new page sitting at the old page's scroll position.
        router.events.on('routeChangeStart', scrollToTop)

        return () => {
            router.events.off('routeChangeStart', scrollToTop)
        }
    }, [router.events])

    return (
        <div className='font-sans'>
            <Head>
                <meta name='viewport' content='width=device-width, initial-scale=1' />
                <style dangerouslySetInnerHTML={{ __html: fontVariables }} />
            </Head>

            <PageTransition />

            <Component {...pageProps} />
        </div>
    )
}

export default App
